import { apiUrl, getApiClient } from '../../api/client';
import type { components, operations } from '../../api/schema';

export type VoiceSalesCampaign = components['schemas']['VoiceSalesCampaignRead'];
export type VoiceSalesCampaignCreateBody = components['schemas']['VoiceSalesCampaignCreateRequest'];
export type VoiceSalesScheduleResponse = components['schemas']['VoiceSalesScheduleResponse'];
type VoiceSalesTargetInternal = components['schemas']['VoiceSalesTargetInternalRead'];
/**
 * Temporary source-compatibility bridge while the Voice Sales UI migrates from
 * opaque target metadata to the typed context/compliance fields. The API does
 * not return these legacy fields; they remain optional only for existing UI
 * fallbacks and fixtures on the shared backend PR lineage.
 */
export type VoiceSalesTarget = VoiceSalesTargetInternal & {
  contact_phone_number_id?: number;
  source_model: string;
  source_id?: number;
  source_field?: string;
  place_id?: number | null;
  report_id?: number | null;
  zip_blast_request_id?: number | null;
  task_id?: number | null;
  metadata_json: Record<string, unknown>;
};
export type VoiceSalesCall = components['schemas']['VoiceSalesCallInternalRead'];
export type VoiceSalesCallDetail = components['schemas']['VoiceSalesCallDetailResponse'];
export type VoiceSalesRecording = components['schemas']['VoiceSalesRecordingMediaRead'];
export type VoiceSalesDncScrubBody = components['schemas']['VoiceSalesDncScrubRequest'];
export type VoiceSalesDncScrubResponse = components['schemas']['VoiceSalesDncScrubResponse'];
export type LocalVoiceSalesScorecard = components['schemas']['LocalVoiceSalesScorecardRead'];
export type LocalVoiceSalesDemoResponse = components['schemas']['LocalVoiceSalesDemoResponse'];

export type VoiceSalesListParams = {
  campaign_id?: number;
  status?: string;
  eligibility_status?: string;
  limit?: number;
  offset?: number;
};

export class VoiceSalesApiError extends Error {
  readonly status: number;

  constructor(message: string, status = 0) {
    super(message);
    this.name = 'VoiceSalesApiError';
    this.status = status;
  }
}

function requestError(message: string, response?: Response): VoiceSalesApiError {
  return new VoiceSalesApiError(message, response?.status ?? 0);
}

export async function listVoiceSalesCampaigns(limit = 100, offset = 0) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/voice-sales/campaigns', {
    params: { query: { limit, offset } },
  });
  if (error) throw requestError('Failed to fetch voice sales campaigns', response);
  return data ?? [];
}

export async function getVoiceSalesCampaign(campaignUuid: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/voice-sales/campaigns/{campaign_uuid}', {
    params: { path: { campaign_uuid: campaignUuid } },
  });
  if (error) throw requestError('Failed to fetch voice sales campaign', response);
  if (!data) throw requestError('Failed to fetch voice sales campaign: empty response', response);
  return data;
}

export async function createVoiceSalesCampaign(body: VoiceSalesCampaignCreateBody) {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/voice-sales/campaigns', { body });
  if (error) throw requestError('Failed to create voice sales campaign', response);
  if (!data) throw requestError('Failed to create voice sales campaign: empty response', response);
  return data;
}

export async function scheduleVoiceSalesCampaign(campaignId: number) {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/voice-sales/campaigns/{campaign_id}/schedule', {
    params: { path: { campaign_id: campaignId } },
  });
  if (error) throw requestError('Failed to schedule voice sales campaign', response);
  if (!data) throw requestError('Failed to schedule voice sales campaign: empty response', response);
  return data;
}

export async function listVoiceSalesTargets(
  params: VoiceSalesListParams = {},
): Promise<VoiceSalesTarget[]> {
  const client = getApiClient();
  const query = {
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
    ...(params.campaign_id ? { campaign_id: params.campaign_id } : {}),
    ...(params.eligibility_status ? { eligibility_status: params.eligibility_status } : {}),
  };
  const { data, error, response } = await client.GET('/voice-sales/targets', {
    params: { query },
  });
  if (error) throw requestError('Failed to fetch voice sales targets', response);
  return (data ?? []).map((target) => ({
    ...target,
    source_model: target.context?.source_type ?? 'unknown',
    metadata_json: {
      source_state: target.compliance.source_state,
      line_type: target.phone_line_type_info?.line_type,
      do_not_call: target.compliance.do_not_call,
      dnc_status: target.compliance.dnc_status,
      dnc_scrubbed_at: target.compliance.dnc_scrubbed_at,
    },
  }));
}

export async function listVoiceSalesCalls(params: VoiceSalesListParams = {}) {
  const client = getApiClient();
  const query = {
    limit: params.limit ?? 100,
    offset: params.offset ?? 0,
    ...(params.campaign_id ? { campaign_id: params.campaign_id } : {}),
    ...(params.status ? { status: params.status } : {}),
  };
  const { data, error, response } = await client.GET('/voice-sales/calls', {
    params: { query },
  });
  if (error) throw requestError('Failed to fetch voice sales calls', response);
  return data ?? [];
}

export async function listLocalVoiceSalesScorecards(limit = 25) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/voice-sales/local-demo/scorecards', {
    params: { query: { limit } },
  });
  if (error) throw requestError('Failed to fetch local scorecards for a real test call', response);
  return data ?? [];
}

export async function createLocalVoiceSalesDemoCall(
  reportId: number,
): Promise<LocalVoiceSalesDemoResponse> {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/voice-sales/local-demo/calls', {
    body: {
      report_id: reportId,
      confirmation: 'CALL_APPROVED_MAX_NUMBER',
    },
  });
  if (error) throw requestError('Failed to queue the local real test call', response);
  if (!data) throw requestError('Failed to queue the local real test call: empty response', response);
  return data;
}

export async function getVoiceSalesCallDetail(callUuid: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/voice-sales/calls/{call_uuid}', {
    params: { path: { call_uuid: callUuid } },
  });
  if (error) throw requestError('Failed to fetch voice sales call detail', response);
  if (!data) throw requestError('Failed to fetch voice sales call detail: empty response', response);
  return data;
}

type RecordingMediaParameters = NonNullable<
  operations['get_voice_sales_recording_media_voice_sales_recordings__recording_uuid__media_get']['parameters']
>;

export async function getVoiceSalesRecordingMedia(recording: VoiceSalesRecording): Promise<Blob> {
  const invalidCapability = new Error(
    'Recording media is not ready or has an invalid secure playback link',
  );
  if (recording.media_state !== 'ready' || !recording.media_url) {
    throw invalidCapability;
  }

  const baseUrl = new URL(apiUrl);
  const mediaUrl = new URL(recording.media_url, baseUrl);
  const expectedPath = `/voice-sales/recordings/${encodeURIComponent(recording.uuid)}/media`;
  const expires = Number(mediaUrl.searchParams.get('expires'));
  const signature = mediaUrl.searchParams.get('signature');

  if (
    mediaUrl.origin !== baseUrl.origin ||
    mediaUrl.pathname !== expectedPath ||
    !Number.isSafeInteger(expires) ||
    expires <= 0 ||
    !signature
  ) {
    throw invalidCapability;
  }

  const query = { expires, signature } satisfies NonNullable<RecordingMediaParameters['query']>;
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-sales/recordings/{recording_uuid}/media', {
    params: {
      path: { recording_uuid: recording.uuid },
      query,
    },
    parseAs: 'blob',
  });
  if (error || !(data instanceof Blob)) {
    throw new Error('Failed to fetch voice sales recording media');
  }
  return data;
}

export async function scrubVoiceSalesDnc(body: VoiceSalesDncScrubBody) {
  const client = getApiClient();
  const { data, error, response } = await client.POST('/voice-sales/DNC/scrub', { body });
  if (error) throw requestError('Failed to run voice sales DNC scrub', response);
  if (!data) throw requestError('Failed to run voice sales DNC scrub: empty response', response);
  return data;
}
